/**
 * Content-review tool — internal-only, English UI (same precedent as
 * Admin.tsx; the one Georgian string is the "შესწორება" fix button, which
 * matches how the owner talks about corrections).
 *
 * The owner reads a whole unit's teaching text on one page (every lesson's
 * ReviewCards, in play order) and files corrections without playing
 * lessons. Each card gets a small fix button → dialog with the card's
 * current text + a textarea → POST /api/admin/corrections. Cards with an
 * open correction show a "flagged ✓" badge + the correction text.
 *
 * Numbering: lessons are shown as "Unit {u}, Lesson {n}" where n is the
 * 1-based play position within the unit — NEVER the content file number.
 *
 * Auth: client-side hint via isAdminEmail (cosmetic 403 panel); the Pages
 * Function re-verifies the Supabase JWT against ADMIN_EMAILS on the server.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Container, Dialog, Heading, Screen, Text, Textarea } from '@fluent/ui';
import type { Lesson } from '@fluent/types';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { useIsAdminEmail } from '../admin/constants.js';
import { getCourseTree, type CourseTreeUnit } from '../content/course.js';
import { blockSnapshot, buildLessonReview, type ReviewBlock } from '../lesson/reviewContent.js';
import { ReviewCard } from '../lesson/ReviewCard.js';
import './ContentReview.css';

interface CorrectionRow {
  id: string;
  created_at: string;
  unit_id: string;
  lesson_id: string;
  display_ref: string;
  block_key: string;
  original_text: string;
  correction_text: string;
  status: 'open' | 'applied' | 'dismissed';
  applied_at: string | null;
}

type CorrectionsState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; rows: CorrectionRow[] };

/** What the fix dialog is editing. */
interface FlagTarget {
  lesson: Lesson;
  block: ReviewBlock;
  displayRef: string;
  unitId: string;
  originalText: string;
}

const CORRECTION_MAX = 4000;

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function ContentReview() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const tree = useMemo(() => getCourseTree(), []);
  const units = useMemo(() => tree?.units ?? [], [tree]);

  const [unitIndex, setUnitIndex] = useState(0);
  const unit: CourseTreeUnit | null = units[unitIndex] ?? null;

  const [corrections, setCorrections] = useState<CorrectionsState>({ kind: 'loading' });
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Fix dialog state
  const [target, setTarget] = useState<FlagTarget | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isAdmin = useIsAdminEmail(user?.email ?? null);

  const loadCorrections = useCallback(async (unitId: string) => {
    setCorrections({ kind: 'loading' });
    try {
      const token = await getAccessToken();
      if (!token) {
        setCorrections({ kind: 'error', message: 'No active session — sign in again.' });
        return;
      }
      const r = await fetch(
        `/api/admin/corrections?unit_id=${encodeURIComponent(unitId)}&status=open`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setCorrections({
          kind: 'error',
          message:
            r.status === 403
              ? 'Not authorized — your email is not in the admin allowlist.'
              : (body.error ?? `HTTP ${r.status}`),
        });
        return;
      }
      const body = (await r.json()) as { corrections: CorrectionRow[] };
      setCorrections({ kind: 'ok', rows: body.corrections });
    } catch (err) {
      setCorrections({
        kind: 'error',
        message: err instanceof Error ? err.message : 'load failed',
      });
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || !unit) return;
    void loadCorrections(unit.id);
  }, [isAdmin, unit, loadCorrections]);

  /** lesson_id → block_key → open correction (newest wins on collision). */
  const openByBlock = useMemo(() => {
    const map = new Map<string, CorrectionRow>();
    if (corrections.kind !== 'ok') return map;
    // rows are created_at desc; iterate backwards so newest overwrites.
    for (let i = corrections.rows.length - 1; i >= 0; i--) {
      const row = corrections.rows[i];
      map.set(`${row.lesson_id}::${row.block_key}`, row);
    }
    return map;
  }, [corrections]);

  if (user && !isAdmin) {
    return (
      <Screen>
        <Container size="md">
          <div className="creview-denied">
            <div className="creview-denied__icon">🔒</div>
            <Heading level={2}>Not authorized</Heading>
            <Text tone="muted">This page is for admins only.</Text>
            <Button variant="neutral" onClick={() => navigate('/')}>
              Back to home
            </Button>
          </div>
        </Container>
      </Screen>
    );
  }

  if (!tree || !unit) {
    return (
      <Screen>
        <Container size="md">
          <p className="creview-status">No course content found.</p>
        </Container>
      </Screen>
    );
  }

  async function saveCorrection() {
    if (!target || !unit) return;
    const text = draft.trim();
    if (!text) {
      setSaveError('Write the correction first.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        setSaveError('No active session — sign in again.');
        return;
      }
      const r = await fetch('/api/admin/corrections', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unit_id: target.unitId,
          lesson_id: target.lesson.id,
          display_ref: target.displayRef,
          block_key: target.block.key,
          original_text: target.originalText,
          correction_text: text,
        }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        ok?: boolean;
        correction?: CorrectionRow;
        error?: string;
      };
      if (!r.ok || !body.ok || !body.correction) {
        setSaveError(body.error ?? `HTTP ${r.status}`);
        return;
      }
      const saved = body.correction;
      // Optimistic merge — the card flips to "flagged ✓" immediately.
      setCorrections((prev) =>
        prev.kind === 'ok' ? { kind: 'ok', rows: [saved, ...prev.rows] } : prev,
      );
      setTarget(null);
      setDraft('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  const openCount = corrections.kind === 'ok' ? corrections.rows.length : 0;
  const unitNumber = unitIndex + 1;

  // Flatten the unit's lessons in play order (skills in order, lessons in
  // order) — position n is what the owner calls "Lesson n".
  const orderedLessons: Lesson[] = unit.resolvedSkills.flatMap((s) => s.resolvedLessons);

  return (
    <Screen>
      <Container size="lg">
        <div className="creview">
          <header className="creview-topbar">
            <h1 className="creview-topbar__title">Content review</h1>
            <span className="creview-topbar__signed-as">
              Signed in as <strong>{user?.email ?? '—'}</strong>
            </span>
          </header>

          <div className="creview-units" role="tablist" aria-label="Units">
            {units.map((u, i) => (
              <Button
                key={u.id}
                variant={i === unitIndex ? 'primary' : 'neutral'}
                size="sm"
                onClick={() => {
                  setUnitIndex(i);
                  setSummaryOpen(false);
                }}
              >
                {`Unit ${i + 1} — ${u.title_en}`}
              </Button>
            ))}
          </div>

          {corrections.kind === 'loading' && (
            <p className="creview-status">Loading open corrections…</p>
          )}
          {corrections.kind === 'error' && (
            <p className="creview-status creview-status--error">
              Error loading corrections: {corrections.message}
            </p>
          )}

          {corrections.kind === 'ok' && (
            <section className="creview-summary">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSummaryOpen((o) => !o)}
                aria-expanded={summaryOpen}
              >
                {summaryOpen ? '▾' : '▸'} Open corrections ({openCount})
              </Button>
              {summaryOpen && (
                <ul className="creview-summary__list">
                  {corrections.rows.length === 0 && (
                    <li className="creview-summary__empty">
                      Nothing flagged in this unit yet.
                    </li>
                  )}
                  {corrections.rows.map((row) => (
                    <li key={row.id} className="creview-summary__item">
                      <span className="creview-summary__ref">{row.display_ref}</span>
                      <span className="creview-summary__text" lang="ka">
                        {row.correction_text}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {orderedLessons.map((lesson, li) => {
            const lessonNumber = li + 1;
            const displayRef = `Unit ${unitNumber}, Lesson ${lessonNumber}`;
            const blocks = buildLessonReview(lesson);
            return (
              <section key={lesson.id} className="creview-lesson">
                <h2 className="creview-lesson__title">
                  {displayRef} — <span lang="ka">{lesson.title}</span>
                </h2>
                <div className="creview-lesson__cards">
                  {blocks.map((block) => {
                    const open = openByBlock.get(`${lesson.id}::${block.key}`);
                    return (
                      <div key={block.key} className="creview-block">
                        <ReviewCard block={block} />
                        <div className="creview-block__bar">
                          {open ? (
                            <span className="creview-flagged">flagged ✓</span>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setTarget({
                                  lesson,
                                  block,
                                  displayRef,
                                  unitId: unit.id,
                                  originalText: blockSnapshot(block),
                                });
                                setDraft('');
                                setSaveError(null);
                              }}
                            >
                              შესწორება
                            </Button>
                          )}
                        </div>
                        {open && (
                          <p className="creview-block__correction" lang="ka">
                            {open.correction_text}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </Container>

      <Dialog
        open={!!target}
        onOpenChange={(o) => {
          if (!o) {
            setTarget(null);
            setDraft('');
            setSaveError(null);
          }
        }}
        title="შესწორება"
        description={target?.displayRef ?? ''}
        actions={
          <>
            <Button
              variant="neutral"
              onClick={() => {
                setTarget(null);
                setDraft('');
                setSaveError(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void saveCorrection()} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        {target && (
          <div className="creview-dialog">
            <pre className="creview-dialog__original">{target.originalText}</pre>
            <Textarea
              label="Correction"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              maxLength={CORRECTION_MAX}
              placeholder="What should it say instead?"
              error={saveError}
            />
          </div>
        )}
      </Dialog>
    </Screen>
  );
}
